import { ColumnSizingState } from '@tanstack/react-table';
import { useState } from 'react';

import { PatientsListColumnName } from '@/shared/config';

import {
  PATIENT_LIST_DEFAULT_COLUMN_SETTINGS,
  PatientListColumnSettings,
} from '../config';
import {
  readPatientListColumnSettings,
  removePatientListColumnSettings,
  writePatientListColumnSettings,
} from '../lib';

export const usePatientListColumnSettings = (organizationID: string) => {
  // The Patients page does not remount on organization switch, so the
  // settings are keyed by the organization they were read for and re-read
  // whenever a different one arrives.
  const [state, setState] = useState(() => ({
    organizationID,
    settings: readPatientListColumnSettings(organizationID),
  }));

  if (state.organizationID !== organizationID) {
    setState({
      organizationID,
      settings: readPatientListColumnSettings(organizationID),
    });
  }

  const persist = (settings: PatientListColumnSettings) => {
    setState({ organizationID, settings });
    writePatientListColumnSettings(organizationID, settings);
  };

  const toggleColumn = (column: PatientsListColumnName) => {
    if (column === PatientsListColumnName.PatientName) {
      return;
    }

    persist({
      ...state.settings,
      visibility: {
        ...state.settings.visibility,
        [column]: state.settings.visibility[column] === false,
      },
    });
  };

  const setColumnWidths = (widths: ColumnSizingState) => {
    persist({
      ...state.settings,
      widths: { ...state.settings.widths, ...widths },
    });
  };

  const reset = () => {
    setState({
      organizationID,
      settings: PATIENT_LIST_DEFAULT_COLUMN_SETTINGS,
    });
    removePatientListColumnSettings(organizationID);
  };

  return {
    columnVisibility: state.settings.visibility,
    columnSizing: state.settings.widths,
    toggleColumn,
    setColumnWidths,
    reset,
  };
};
